import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  Inventory,
  Warning,
  Refresh,
  Business,
  TrendingDown,
  CalendarToday,
  Calculate,
  Add,
  Edit,
  Delete,
  Save,
} from '@mui/icons-material';

interface StockAlert {
  id: string;
  productCategory: string;
  lowStockThreshold: number;
  criticalStockThreshold: number;
  reorderPoint: number;
  reorderQuantity: number;
}

interface Supplier {
  id: string;
  name: string;
  contactPerson: string;
  phone: string;
  email: string;
  category: string;
  isPreferred: boolean;
  paymentTerms: string;
}

interface InventoryManagementSettingsProps {
  salonId: string;
}

const InventoryManagementSettings: React.FC<InventoryManagementSettingsProps> = ({ salonId }) => {
  const [inventorySettings, setInventorySettings] = useState({
    enableInventoryTracking: true,
    enableLowStockAlerts: true,
    enableExpiryTracking: true,
    enableAutomaticReorder: false,
    costCalculationMethod: 'fifo', // 'fifo' | 'lifo' | 'average'
    defaultMarkupPercentage: 100,
  });

  const [stockAlerts, setStockAlerts] = useState<StockAlert[]>([
    {
      id: '1',
      productCategory: 'Makeup Products',
      lowStockThreshold: 10,
      criticalStockThreshold: 5,
      reorderPoint: 8,
      reorderQuantity: 50,
    },
    {
      id: '2',
      productCategory: 'Hair Care Products',
      lowStockThreshold: 15,
      criticalStockThreshold: 5,
      reorderPoint: 10,
      reorderQuantity: 30,
    },
    {
      id: '3',
      productCategory: 'Skincare Products',
      lowStockThreshold: 12,
      criticalStockThreshold: 3,
      reorderPoint: 8,
      reorderQuantity: 25,
    },
  ]);

  const [suppliers, setSuppliers] = useState<Supplier[]>([
    {
      id: '1',
      name: 'Beauty Supplies Co.',
      contactPerson: 'Rajesh Kumar',
      phone: '+91 98765 43210',
      email: 'rajesh@beautysupplies.com',
      category: 'Makeup Products',
      isPreferred: true,
      paymentTerms: '30 days',
    },
    {
      id: '2',
      name: 'Professional Hair Products',
      contactPerson: 'Priya Sharma',
      phone: '+91 98765 43211',
      email: 'priya@hairproducts.com',
      category: 'Hair Care Products',
      isPreferred: true,
      paymentTerms: '15 days',
    },
    {
      id: '3',
      name: 'Skincare Solutions Ltd.',
      contactPerson: 'Amit Patel',
      phone: '+91 98765 43212',
      email: 'amit@skincaresolutions.com',
      category: 'Skincare Products',
      isPreferred: false,
      paymentTerms: '45 days',
    },
  ]);

  const [expirySettings, setExpirySettings] = useState({
    enableExpiryAlerts: true,
    expiryWarningDays: 30,
    criticalExpiryDays: 7,
    autoRemoveExpired: false,
    expiryCheckFrequency: 'daily', // 'daily' | 'weekly' | 'monthly'
  });

  const [reorderSettings, setReorderSettings] = useState({
    enableAutoReorder: false,
    reorderLeadTimeDays: 7,
    safetyStockPercentage: 20,
    minimumOrderValue: 5000,
    preferredSupplierOnly: true,
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleInventorySettingChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setInventorySettings(prev => ({ ...prev, [field]: value }));
  };

  const handleExpirySettingChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setExpirySettings(prev => ({ ...prev, [field]: value }));
  };

  const handleReorderSettingChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setReorderSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }, 1000);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#2D3748', mb: 3 }}>
        Inventory Management Settings
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Inventory management settings saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* General Inventory Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Inventory sx={{ color: '#E8B4B8', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  General Settings
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={inventorySettings.enableInventoryTracking}
                        onChange={handleInventorySettingChange('enableInventoryTracking')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Enable Inventory Tracking"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={inventorySettings.enableLowStockAlerts}
                        onChange={handleInventorySettingChange('enableLowStockAlerts')}
                        disabled={!inventorySettings.enableInventoryTracking}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Enable Low Stock Alerts"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={inventorySettings.enableExpiryTracking}
                        onChange={handleInventorySettingChange('enableExpiryTracking')}
                        disabled={!inventorySettings.enableInventoryTracking}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Enable Expiry Tracking"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!inventorySettings.enableInventoryTracking}>
                    <InputLabel>Cost Calculation Method</InputLabel>
                    <Select
                      value={inventorySettings.costCalculationMethod}
                      label="Cost Calculation Method"
                      onChange={(e) => setInventorySettings(prev => ({ ...prev, costCalculationMethod: e.target.value }))}
                    >
                      <MenuItem value="fifo">FIFO (First In, First Out)</MenuItem>
                      <MenuItem value="lifo">LIFO (Last In, First Out)</MenuItem>
                      <MenuItem value="average">Weighted Average</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Default Markup Percentage (%)"
                    type="number"
                    value={inventorySettings.defaultMarkupPercentage}
                    onChange={handleInventorySettingChange('defaultMarkupPercentage')}
                    disabled={!inventorySettings.enableInventoryTracking}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Expiry Management */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CalendarToday sx={{ color: '#E8B4B8', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Expiry Management
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={expirySettings.enableExpiryAlerts}
                        onChange={handleExpirySettingChange('enableExpiryAlerts')}
                        disabled={!inventorySettings.enableExpiryTracking}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Enable Expiry Alerts"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Warning Days Before Expiry"
                    type="number"
                    value={expirySettings.expiryWarningDays}
                    onChange={handleExpirySettingChange('expiryWarningDays')}
                    disabled={!expirySettings.enableExpiryAlerts}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Critical Expiry Days"
                    type="number"
                    value={expirySettings.criticalExpiryDays}
                    onChange={handleExpirySettingChange('criticalExpiryDays')}
                    disabled={!expirySettings.enableExpiryAlerts}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!expirySettings.enableExpiryAlerts}>
                    <InputLabel>Expiry Check Frequency</InputLabel>
                    <Select
                      value={expirySettings.expiryCheckFrequency}
                      label="Expiry Check Frequency"
                      onChange={(e) => setExpirySettings(prev => ({ ...prev, expiryCheckFrequency: e.target.value }))}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={expirySettings.autoRemoveExpired}
                        onChange={handleExpirySettingChange('autoRemoveExpired')}
                        disabled={!expirySettings.enableExpiryAlerts}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Auto Remove Expired Items"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Stock Alert Thresholds */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Warning sx={{ color: '#E8B4B8', mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Stock Alert Thresholds
                  </Typography>
                </Box>
                <Button
                  startIcon={<Add />}
                  variant="outlined"
                  sx={{
                    borderColor: '#E8B4B8',
                    color: '#E8B4B8',
                    '&:hover': {
                      borderColor: '#D69BA0',
                      bgcolor: 'rgba(232, 180, 184, 0.05)',
                    },
                  }}
                >
                  Add Category
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Low Stock</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Critical Stock</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reorder Point</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reorder Qty</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockAlerts.map((alert) => (
                      <TableRow key={alert.id}>
                        <TableCell>{alert.productCategory}</TableCell>
                        <TableCell>{alert.lowStockThreshold}</TableCell>
                        <TableCell>{alert.criticalStockThreshold}</TableCell>
                        <TableCell>{alert.reorderPoint}</TableCell>
                        <TableCell>{alert.reorderQuantity}</TableCell>
                        <TableCell>
                          <IconButton size="small" sx={{ color: '#E8B4B8', mr: 1 }}>
                            <Edit />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Supplier Management */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Business sx={{ color: '#E8B4B8', mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Supplier Management
                  </Typography>
                </Box>
                <Button
                  startIcon={<Add />}
                  variant="outlined"
                  sx={{
                    borderColor: '#E8B4B8',
                    color: '#E8B4B8',
                    '&:hover': {
                      borderColor: '#D69BA0',
                      bgcolor: 'rgba(232, 180, 184, 0.05)',
                    },
                  }}
                >
                  Add Supplier
                </Button>
              </Box>

              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Supplier Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Contact Person</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Payment Terms</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suppliers.map((supplier) => (
                      <TableRow key={supplier.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2">{supplier.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {supplier.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{supplier.contactPerson}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {supplier.phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{supplier.category}</TableCell>
                        <TableCell>{supplier.paymentTerms}</TableCell>
                        <TableCell>
                          <Chip 
                            label={supplier.isPreferred ? 'Preferred' : 'Regular'} 
                            size="small" 
                            color={supplier.isPreferred ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" sx={{ color: '#E8B4B8', mr: 1 }}>
                            <Edit />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Automatic Reorder Settings */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Refresh sx={{ color: '#E8B4B8', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Automatic Reorder Settings
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reorderSettings.enableAutoReorder}
                        onChange={handleReorderSettingChange('enableAutoReorder')}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Enable Auto Reorder"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Lead Time (days)"
                    type="number"
                    value={reorderSettings.reorderLeadTimeDays}
                    onChange={handleReorderSettingChange('reorderLeadTimeDays')}
                    disabled={!reorderSettings.enableAutoReorder}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Safety Stock (%)"
                    type="number"
                    value={reorderSettings.safetyStockPercentage}
                    onChange={handleReorderSettingChange('safetyStockPercentage')}
                    disabled={!reorderSettings.enableAutoReorder}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Min Order Value (₹)"
                    type="number"
                    value={reorderSettings.minimumOrderValue}
                    onChange={handleReorderSettingChange('minimumOrderValue')}
                    disabled={!reorderSettings.enableAutoReorder}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reorderSettings.preferredSupplierOnly}
                        onChange={handleReorderSettingChange('preferredSupplierOnly')}
                        disabled={!reorderSettings.enableAutoReorder}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E8B4B8',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E8B4B8',
                          },
                        }}
                      />
                    }
                    label="Use Preferred Suppliers Only"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: '#E8B4B8',
                color: 'white',
                px: 4,
                py: 1.5,
                borderRadius: 3,
                fontWeight: 600,
                '&:hover': { bgcolor: '#D69BA0' },
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InventoryManagementSettings;